import { VerticalSpace } from '@brokerbay/design-components';
import axios from 'axios';
import {
  Button,
  Checkbox,
  Input,
  message,
  Page,
  Space,
  Text,
} from 'components';
import { AuthContext } from 'contexts';
import React from 'react';
import styled from 'styled-components';
import { OBJECT_ID_REGEX } from 'utils/constants';

const API_URL = '/transfer/qa_page/automated_transfer';

const buildUrl = (
  orgIds: string[],
  skipVerification: boolean,
  skipTeams: boolean,
) => {
  const filteredIds = orgIds.filter(Boolean);
  const searchParams = new URLSearchParams();
  if (filteredIds.length) {
    searchParams.set('orgIds', filteredIds.map(s => s.trim()).join(','));
  }
  if (skipVerification) searchParams.set('skipVerification', 'true');
  if (skipTeams) searchParams.set('skipTeams', 'true');
  return `${API_URL}?${searchParams.toString()}`;
};

const buildFullUrl = (
  orgIds: string[],
  skipVerification: boolean,
  skipTeams: boolean,
) => `${window.location.host}${buildUrl(
    orgIds,
    skipVerification,
    skipTeams,
  )}`;

const AutomatedTransfersPage = () => {
  const { auth } = React.useContext(AuthContext);
  const [isLoading, setIsLoading] = React.useState(false);
  const [orgIds, setOrgIds] = React.useState([auth!.org.id || '']);
  const [skipVerification, setSkipVerification] = React.useState(false);
  const [skipTeams, setSkipTeams] = React.useState(false);
  const [results, setResults] = React.useState<any[]>([]);

  const fire = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const filteredIds = orgIds.filter(Boolean).map(s => s.trim());
      if (filteredIds.length) {
        const badId = filteredIds.find(id => !id.match(OBJECT_ID_REGEX));
        if (badId) {
          throw new Error(`${badId} is not a valid object id`);
        }
      }
      const url = buildUrl(orgIds, skipVerification, skipTeams);
      setIsLoading(true);
      const result = await axios.get(url);
      setResults(prev => [result.data, ...prev]);
      message.success('Cron Finished');
    } catch (error) {
      message.error(error?.toString(), 8);
    } finally {
      setIsLoading(false);
    }
  };

  const fullUrl = buildFullUrl(orgIds, skipVerification, skipTeams);

  return (
    <Page type="filled" title="Automated Transfers QA Page">
      <Layout>
        <Text>
          This will trigger the automated transfers cron job (AKA: Genesis)
        </Text>
        <Text>
          Multiple org ids can be specified (optional but recommended) to limit
          the cron to specified orgs. Note that by default this will run other
          jobs that are normally run during the Genesis process but this can be
          toggled off
        </Text>
        <form onSubmit={fire}>
          <VerticalSpace>
            {orgIds.map((el, index) => (
              <StyledInput
                key={index}
                addonAfter={
                  index === 0 ? (
                    <Space size="large">
                      <Button onClick={() => setOrgIds(prev => [...prev, ''])}>
                        +
                      </Button>
                    </Space>
                  ) : (
                    <Button
                      onClick={() =>
                        setOrgIds(prev =>
                          prev.slice(0, index).concat(prev.slice(index + 1)),
                        )
                      }
                    >
                      -
                    </Button>
                  )
                }
                value={orgIds[index]}
                onChange={e => {
                  const { value } = e.target;
                  setOrgIds(prev =>
                    prev
                      .slice(0, index)
                      .concat([value])
                      .concat(prev.slice(index + 1)),
                  );
                }}
                placeholder="Org Id"
              />
            ))}

            <Checkbox
              checked={skipVerification}
              onChange={e => setSkipVerification(e.target.checked)}
            >
              Skip verifying users
            </Checkbox>

            <Checkbox
              checked={skipTeams}
              onChange={e => setSkipTeams(e.target.checked)}
            >
              Skip automatically updating teams (affects all orgs not just the
              specified ones)
            </Checkbox>

            <Button disabled={isLoading} loading={isLoading} htmlType="submit">
              Trigger cron job
            </Button>
          </VerticalSpace>
        </form>
        <Space>
          <Text>API route: {fullUrl}</Text>
          <Button onClick={() => navigator.clipboard.writeText(fullUrl)}>
            Copy Url
          </Button>
        </Space>
        {(results.length || null) && (
          <>
            Result Log:
            <pre>{JSON.stringify(results, null, 2)}</pre>
          </>
        )}
      </Layout>
    </Page>
  );
};

export default AutomatedTransfersPage;

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 10px;
  padding: 20px;
  background: white;
`;

const StyledInput = styled(Input)`
  width: 500px;
`;
